// Meta Pixel utility — uses BOTH the official fbq() pipeline AND a direct
// image-pixel fallback to facebook.com/tr.
//
// Why the fallback?
//   1. Meta Pixel Helper sometimes doesn't detect dynamically-injected pixels.
//   2. fbq() only QUEUES events until fbevents.js finishes loading. If the
//      user clicks a CTA with target="_blank" before the script finishes,
//      the new tab can open before the queued request is sent.
//   3. The direct image request mirrors Meta's official <noscript><img>
//      fallback and fires synchronously, so events ALWAYS reach Meta
//      Events Manager regardless of script timing.

const PIXEL_ID = '794743720241228'

declare global {
  interface Window {
    fbq: any
    _fbq: any
  }
}

let initialized = false

/**
 * Fires a direct image-pixel request to facebook.com/tr — the same endpoint
 * the official Meta noscript fallback uses. This is synchronous-ish and
 * guaranteed to be sent before the page navigates.
 */
function fireDirectPixel(eventName: string, params?: Record<string, any>) {
  if (typeof window === 'undefined') return
  try {
    const url = new URL('https://www.facebook.com/tr/')
    url.searchParams.set('id', PIXEL_ID)
    url.searchParams.set('ev', eventName)
    url.searchParams.set('dl', window.location.href)
    url.searchParams.set('rl', document.referrer || '')
    url.searchParams.set('if', 'false')
    url.searchParams.set('ts', String(Date.now()))
    if (params) {
      url.searchParams.set(
        'cd[content_name]',
        String(params.content_name || ''),
      )
      url.searchParams.set(
        'cd[content_type]',
        String(params.content_type || ''),
      )
      url.searchParams.set('cd[value]', String(params.value || ''))
      url.searchParams.set('cd[currency]', String(params.currency || ''))
      if (Array.isArray(params.content_ids)) {
        url.searchParams.set(
          'cd[content_ids]',
          JSON.stringify(params.content_ids),
        )
      }
    }
    // Use sendBeacon when available so the request survives a tab navigation
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url.toString())
    } else {
      const img = new Image(1, 1)
      img.src = url.toString()
    }
  } catch {
    /* noop */
  }
}

export function initMetaPixel() {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  try {
    // 1. Set up the synchronous fbq queue
    ;(function (f: any, b: any, e: string, v: string) {
      if (f.fbq) return
      const n: any = (f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments)
      })
      if (!f._fbq) f._fbq = n
      n.push = n
      n.loaded = true
      n.version = '2.0'
      n.queue = []
      const t = b.createElement(e) as HTMLScriptElement
      t.async = true
      t.src = v
      t.onerror = () => {
        /* Tracker blockers — fail silently. We still have the direct fallback. */
      }
      const s = b.getElementsByTagName(e)[0]
      s.parentNode!.insertBefore(t, s)
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js',
    )

    window.fbq('init', PIXEL_ID)
    window.fbq('track', 'PageView')
  } catch {
    /* noop */
  }

  // 2. Direct image-pixel fallback for PageView (also helps Pixel Helper detect us)
  fireDirectPixel('PageView')

  // 3. Inject the official <noscript> fallback image too — required for full
  //    compliance with Meta's standard pixel install.
  try {
    const noscript = document.createElement('noscript')
    const img = document.createElement('img')
    img.height = 1
    img.width = 1
    img.style.display = 'none'
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`
    noscript.appendChild(img)
    document.body.appendChild(noscript)
  } catch {
    /* noop */
  }
}

export function trackEvent(eventName: string, params?: Record<string, any>) {
  if (typeof window === 'undefined') return

  // Make sure the queue stub exists so future page loads don't break.
  if (!window.fbq) {
    initMetaPixel()
  }

  // 1. Standard fbq path (queues until fbevents.js arrives, then flushes)
  try {
    if (params) {
      window.fbq('track', eventName, params)
    } else {
      window.fbq('track', eventName)
    }
  } catch {
    /* noop */
  }

  // 2. Direct image-pixel fallback — guaranteed to send before navigation,
  //    survives target="_blank" via sendBeacon, and shows up in Network tab
  //    + Pixel Helper + Meta Events Manager.
  fireDirectPixel(eventName, params)
}
